import { ONBOARDED_PREFIX } from '@app/constants/app/storage';

/**
 * Whether this person has seen the first-login welcome yet.
 *
 * Client-side only, like `visitedSprints`: nothing about it belongs to the account, and there is no
 * API for "has been greeted". What the wizard actually *sets* — the switch, the connections — is the
 * API's, and lives there; this is only the flag that decides whether to open it.
 *
 * Keyed by user, because a shared browser has more than one of them and being greeted is a fact about
 * the person, not the machine. Sign-out preserves anything under this prefix, or the welcome would come
 * back on every single sign-in.
 */
const keyFor = (userId?: string): string => `${ONBOARDED_PREFIX}${userId ?? 'unknown'}`;

export const hasOnboarded = (userId?: string): boolean => {
  try {
    return localStorage.getItem(keyFor(userId)) === '1';
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Greeting somebody twice is a far
    // smaller problem than an app that will not render.
    return true;
  }
};

export const markOnboarded = (userId?: string): void => {
  try {
    localStorage.setItem(keyFor(userId), '1');
  } catch {
    // Nothing to do: the welcome simply shows again next time.
  }
};

/** Dev-only: show the welcome again on the next load. Reachable from Settings → Phill bot. */
export const resetOnboarding = (userId?: string): void => {
  try {
    localStorage.removeItem(keyFor(userId));
  } catch {
    // Same as above.
  }
};
